import dayjs from 'dayjs';
import { TimeModule } from '../types/TimeModule';

/** Primitive time range interface to allow using plain objects */
export interface TimeRangeLike {
  startTime: string; // format HH:mm
  endTime: string; // format HH:mm
}

/**
 * Compare two HH:mm strings.
 * Returns negative if a < b, 0 if equal, positive if a > b.
 */
export function compareTimes(a: string, b: string): number {
  const [ah, am] = a.split(':').map(Number);
  const [bh, bm] = b.split(':').map(Number);
  if (ah !== bh) {
    return ah - bh;
  }
  return am - bm;
}

/** Validate that a time range has start < end (exclusive) */
export function isValidRange(range: TimeRangeLike): boolean {
  return compareTimes(range.endTime, range.startTime) > 0;
}

/** Classic half-open interval overlap check: [aStart, aEnd) vs [bStart, bEnd) */
export function intervalsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return compareTimes(aStart, bEnd) < 0 && compareTimes(bStart, aEnd) < 0;
}

/** Convenience overload to accept TimeModule or TimeRangeLike */
export function rangesOverlap(
  a: Pick<TimeModule, 'startTime' | 'endTime'> | TimeRangeLike,
  b: Pick<TimeModule, 'startTime' | 'endTime'> | TimeRangeLike,
): boolean {
  return intervalsOverlap(a.startTime, a.endTime, b.startTime, b.endTime);
}

/**
 * Determine if the given range is currently running at server offset.
 * Treats interval as [start, end) (inclusive start, exclusive end).
 */
export function isRangeRunning(
  range: Pick<TimeModule, 'startTime' | 'endTime'> | TimeRangeLike,
  date: string, // 'YYYY-MM-DD'
  offset: number, // server UTC offset in minutes or hours? Assumed dayjs utcOffset compatible (minutes)
): boolean {
  if (!isValidRange(range)) {
    return false;
  }
  const current = dayjs().utcOffset(offset);
  if (current.format('YYYY-MM-DD') !== dayjs(date).format('YYYY-MM-DD')) {
    return false;
  }
  const currentTime = current.format('HH:mm');
  return (
    compareTimes(currentTime, range.startTime) >= 0 &&
    compareTimes(currentTime, range.endTime) < 0
  );
}

/**
 * Utility to wrap raw start/end times as a range-like object.
 */
export function makeRange(startTime: string, endTime: string): TimeRangeLike {
  return { startTime, endTime };
}
